import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

type AxisStatus = 'untested' | 'aligned' | 'drift-left' | 'drift-right';
type PeepSize = '1/8' | '5/32' | '3/16' | '1/4' | 'unspecified';
type PeepAlign = 'untested' | 'centered' | 'rotated-left' | 'rotated-right';

interface SightPeepData {
  // Peep
  peepSize?: PeepSize;
  peepAlign?: PeepAlign;
  peepAntiRot?: 'none' | 'tubing' | 'knot';

  // 1st axis — vertical alignment of sight to riser
  axis1?: AxisStatus;
  // 2nd axis — bubble level (cant)
  axis2?: 'untested' | 'level' | 'off';
  // 3rd axis — the tree-stand killer
  axis3?: AxisStatus;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 10.5 — Sight & Peep Calibration
 *
 * Added in v118 after the School of Nock gap analysis named this the single
 * biggest hole in The Forge's Tune wizard. Dudley dedicates twelve-plus
 * episodes to sight axis calibration alone, and a misaligned 3rd axis is
 * the single most common reason a perfectly paper-tuned, bareshaft-tuned,
 * walk-back-tuned compound still misses left at distance from a tree stand.
 *
 * What we cover (Dudley's order):
 *   1. Peep size + anti-rotation — without a stable, correctly-sized peep,
 *      every axis measurement that follows is contaminated by sight-picture
 *      jitter and rotational drift between shots.
 *   2. 1st axis — vertical alignment of the sight to the riser. Adjusts
 *      with the sight mount bolts or extension bar shim. Affects long-range
 *      left/right.
 *   3. 2nd axis — bubble level cant. Set the bow plumb, level the bubble.
 *      Cant errors amplify with distance and amplify hard on uphill /
 *      downhill shots.
 *   4. 3rd axis — pin housing tilt relative to the riser. This is the
 *      tree-stand killer: paper, bareshaft, and walk-back all pass at level
 *      ground; the third axis only shows up when you tilt the bow down (or
 *      up). Test by levelling the bow plumb in a vise, tilting it 30°
 *      forward, and watching whether the sight bubble stays level. If the
 *      bubble drifts, the 3rd axis is off.
 *
 * Step placement: between nock_tuning and walk_back. Walk-back will surface
 * false L/R signals if 1st or 3rd axis is off, so we calibrate first.
 */
export function SightPeepStep({ tuning }: Props) {
  const data =
    (tuning.session.stepData['sight_peep'] as SightPeepData | undefined) ?? {};
  const update = (patch: Partial<SightPeepData>) =>
    tuning.updateStepData<SightPeepData>('sight_peep', { ...data, ...patch });

  const peepOk =
    data.peepSize !== undefined &&
    data.peepSize !== 'unspecified' &&
    data.peepAlign === 'centered' &&
    (data.peepAntiRot === 'tubing' || data.peepAntiRot === 'knot');

  const axesOk =
    data.axis1 === 'aligned' &&
    data.axis2 === 'level' &&
    data.axis3 === 'aligned';

  return (
    <>
      <GoalCard title="Sight axis calibration — the tree-stand killer">
        Sight axis is the most-skipped step in a pro-shop tune. A misaligned
        third axis is the single most common reason a perfectly paper-tuned,
        bareshaft-tuned, walk-back-tuned compound still misses left at distance
        from a tree stand. Paper and bareshaft pass at level ground; the third
        axis only shows up when you tilt the bow forward. Get this right BEFORE
        the walk-back tune or you'll chase phantom rest adjustments.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">1 · Peep size, alignment, anti-rotation</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          Without a stable peep, every axis measurement after this is
          contaminated by sight-picture jitter. Common sizes: 1/8" indoor / close
          range, 5/32" most hunting, 3/16" low-light hunting, 1/4" extreme
          low-light. Anti-rotation tubing (or a tied serving knot) keeps the
          peep oriented the same way every draw.
        </p>

        <Field label="Peep diameter">
          <select
            value={data.peepSize ?? 'unspecified'}
            onChange={(e) =>
              update({ peepSize: e.target.value as SightPeepData['peepSize'] })
            }
            className={inputClass}
          >
            <option value="unspecified">Not specified</option>
            <option value="1/8">1/8" · indoor / close-range / target</option>
            <option value="5/32">5/32" · most hunting</option>
            <option value="3/16">3/16" · low-light hunting</option>
            <option value="1/4">1/4" · extreme low-light</option>
          </select>
        </Field>

        <div className="mt-3">
          <Field label="At full draw, does the peep center the scope housing?">
            <select
              value={data.peepAlign ?? 'untested'}
              onChange={(e) =>
                update({ peepAlign: e.target.value as PeepAlign })
              }
              className={inputClass}
            >
              <option value="untested">Not tested yet</option>
              <option value="centered">Centered · scope housing is concentric</option>
              <option value="rotated-left">Rotates LEFT · peep is twisting away from neutral</option>
              <option value="rotated-right">Rotates RIGHT · peep is twisting away from neutral</option>
            </select>
          </Field>
          {data.peepAlign === 'rotated-left' && (
            <Status>
              Add a half-twist to the string at the peep (twist in the same
              direction as the rotation you want to correct). Or remove
              twists below the peep. Re-test at full draw. Two or three
              half-twists usually does it.
            </Status>
          )}
          {data.peepAlign === 'rotated-right' && (
            <Status>
              Mirror of the left case — add a half-twist in the opposite
              direction, or remove twists above the peep. Re-test at full draw.
            </Status>
          )}
          {data.peepAlign === 'centered' && (
            <Status ok>
              Peep is rotating correctly. Now lock it in with anti-rotation.
            </Status>
          )}
        </div>

        <div className="mt-3">
          <Field label="Anti-rotation method">
            <select
              value={data.peepAntiRot ?? 'none'}
              onChange={(e) =>
                update({ peepAntiRot: e.target.value as SightPeepData['peepAntiRot'] })
              }
              className={inputClass}
            >
              <option value="none">None · peep is free to rotate</option>
              <option value="tubing">Surgical tubing tied to cable</option>
              <option value="knot">Tied serving knot above &amp; below peep</option>
            </select>
          </Field>
          {data.peepAntiRot === 'none' && (
            <Status>
              Free-rotating peeps drift over time as the string settles. Add
              tubing (faster) or tie serving knots above and below (cleaner,
              quieter, preferred for hunting). Either way, lock the peep in
              its centered orientation permanently.
            </Status>
          )}
          {peepOk && (
            <Status ok>
              Peep is sized, centered, and locked. Move to axis calibration.
            </Status>
          )}
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">2 · 1st axis — vertical sight alignment</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          The 1st axis is the vertical line through your sight as it mounts to
          the riser. If the sight bar is tilted left or right of vertical
          relative to the riser, your pin will drift left or right with
          distance. Test: shoot a tight group at 60+ yards with your bubble
          centered. Then deliberately shoot a group with the bow canted 10° one
          way and 10° the other. The group should move predictably; if it
          doesn't track, the 1st axis is off.
        </p>

        <Field label="1st axis test result">
          <select
            value={data.axis1 ?? 'untested'}
            onChange={(e) => update({ axis1: e.target.value as AxisStatus })}
            className={inputClass}
          >
            <option value="untested">Not tested yet</option>
            <option value="aligned">Tracks correctly · 1st axis is good</option>
            <option value="drift-left">Groups drift LEFT at distance</option>
            <option value="drift-right">Groups drift RIGHT at distance</option>
          </select>
        </Field>

        {data.axis1 === 'drift-left' && (
          <Status>
            Loosen the sight mount bolts, tilt the sight bar TOP-LEFT a few
            thousandths (toward the direction you want the pin to drift back).
            Re-tighten and re-test at 60+ yards. Many slider sights have an
            adjustment bolt specifically for 1st axis — check your sight manual.
          </Status>
        )}
        {data.axis1 === 'drift-right' && (
          <Status>
            Mirror: tilt the sight bar TOP-RIGHT a few thousandths and re-test.
          </Status>
        )}
        {data.axis1 === 'aligned' && (
          <Status ok>1st axis tracks. Move to the bubble level.</Status>
        )}
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">3 · 2nd axis — bubble level (cant)</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          The 2nd axis is your sight's bubble level itself. Set the bow plumb
          in a vise or against a known-vertical reference (door frame works in a
          pinch). The bubble should center exactly. If it doesn't, the bubble is
          mis-set — adjust it now, because every uphill / downhill shot you take
          will lean on it.
        </p>

        <Field label="With the bow plumb, is the bubble centered?">
          <select
            value={data.axis2 ?? 'untested'}
            onChange={(e) =>
              update({ axis2: e.target.value as SightPeepData['axis2'] })
            }
            className={inputClass}
          >
            <option value="untested">Not tested yet</option>
            <option value="level">Bubble is centered · 2nd axis is good</option>
            <option value="off">Bubble is off-center · 2nd axis needs adjustment</option>
          </select>
        </Field>

        {data.axis2 === 'off' && (
          <Status>
            Most sights have a small bubble-level adjustment screw on the
            housing or under a small cover. Loosen, slide the bubble to center
            with the bow held plumb, re-lock. Verify on a fresh plumb hold.
          </Status>
        )}
        {data.axis2 === 'level' && (
          <Status ok>Bubble agrees with plumb. Now the big one — 3rd axis.</Status>
        )}
      </Card>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">4 · 3rd axis — the tree-stand killer</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400">
            Hunter-critical
          </span>
        </div>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          The 3rd axis is the tilt of your pin housing (or scope) relative to
          the riser plane. Paper, bareshaft, and walk-back tunes all pass with a
          mis-aligned 3rd axis at level ground because they're shot horizontal.
          The error only appears when you tilt the bow forward — like every
          tree-stand shot. THIS is why your perfectly tuned bow misses left at
          25 yards from 20 feet up.
        </p>

        <InfoCard title="How to test 3rd axis (no special tools)">
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Set the bow plumb in a vise (or against a vertical door frame).</li>
            <li>Confirm the bubble is centered (2nd axis check, above).</li>
            <li>Tilt the bow forward 30° — same angle as a tree-stand shot down.</li>
            <li>
              Re-check the bubble. If it stays centered → 3rd axis is correct. If
              it drifts left or right → 3rd axis needs adjustment.
            </li>
          </ol>
          <p className="text-[11px] text-zinc-400 mt-2">
            For a more precise test, use a 3rd-axis tuning tool (Hamskea makes
            one) or two plumb-line lasers crossed at the target.
          </p>
        </InfoCard>

        <div className="mt-3">
          <Field label="3rd axis test result">
            <select
              value={data.axis3 ?? 'untested'}
              onChange={(e) => update({ axis3: e.target.value as AxisStatus })}
              className={inputClass}
            >
              <option value="untested">Not tested yet</option>
              <option value="aligned">Bubble stays centered when tilted · 3rd axis good</option>
              <option value="drift-left">Bubble drifts LEFT when tilted forward</option>
              <option value="drift-right">Bubble drifts RIGHT when tilted forward</option>
            </select>
          </Field>
        </div>

        {data.axis3 === 'drift-left' && (
          <Status>
            Your pin housing leans CW around the sight bar (viewed from
            behind). Loosen the 3rd axis adjustment screw on the sight head
            (most modern slider and multi-pin sights have one), rotate the
            housing CCW a few thousandths, re-lock, re-test at the same 30°
            tilt. Repeat until the bubble holds center through the tilt.
          </Status>
        )}
        {data.axis3 === 'drift-right' && (
          <Status>
            Mirror — rotate the pin housing CW a few thousandths around the
            sight bar. Re-test at the same forward tilt.
          </Status>
        )}
        {data.axis3 === 'aligned' && (
          <Status ok>
            3rd axis holds. This is the test most hunters never run. You're
            tree-stand ready. Move to Walk-Back with confidence — anything you
            see there is real walk-back signal, not 3rd-axis contamination.
          </Status>
        )}
      </Card>

      {peepOk && axesOk && (
        <InfoCard title="All four checks pass — sight is calibrated">
          Peep is sized, centered, and locked. 1st axis tracks. Bubble matches
          plumb. 3rd axis holds through forward tilt. Walk-Back tune signals
          from here on will be genuine left/right drift, not axis contamination.
        </InfoCard>
      )}

      <WarningCard title="Re-check after any sight movement">
        Any time you remove or re-mount the sight (transport, travel,
        bumped at the range), re-run the 1st, 2nd, and 3rd axis checks before
        you shoot at distance. Five minutes here saves a missed elk.
      </WarningCard>

      <TipCard title="A note on extension bars">
        Long extension bars (more sight radius) amplify both your sight picture
        AND your axis errors. If you run a 12"+ extension, treat 3rd axis as a
        pre-hunt ritual, not a one-time check. Slider sights with very long
        sliders (10"+) are also more sensitive to 1st axis drift — re-verify
        every season.
      </TipCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div
      className={`mt-2 text-sm leading-relaxed p-2.5 rounded border ${
        ok
          ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300'
          : 'border-amber-700/40 bg-amber-950/20 text-amber-300'
      }`}
    >
      {children}
    </div>
  );
}
